import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { purchaseOrderApi, vendorApi } from '@/lib/api';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Truck, 
  Package, 
  Calendar, 
  User,
  CheckCircle,
  XCircle,
  Clock,
  Send,
  Trash2,
  FileDown,
  Mail
} from 'lucide-react';
import { format } from 'date-fns';

const statusConfig = {
  draft: { label: 'Draft', class: 'status-draft', icon: Clock },
  pending: { label: 'Pending Approval', class: 'status-pending', icon: Clock },
  approved: { label: 'Approved', class: 'status-approved', icon: CheckCircle },
  ordered: { label: 'Ordered', class: 'status-ordered', icon: Send },
  delivered: { label: 'Delivered', class: 'status-delivered', icon: CheckCircle },
};

const statusFlow = ['draft', 'pending', 'approved', 'ordered', 'delivered'];

export default function ViewPO() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [notifyingVendor, setNotifyingVendor] = useState(false);

  const fetchData = async () => {
    try {
      const orderRes = await purchaseOrderApi.getById(id);
      setOrder(orderRes.data);
      
      const vendorRes = await vendorApi.getById(orderRes.data.vendor_id);
      setVendor(vendorRes.data);
    } catch (error) {
      console.error('Error fetching order:', error);
      toast.error('Failed to load purchase order');
      navigate('/purchase-orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, navigate]);

  const handleStatusUpdate = async (newStatus) => {
    setUpdating(true);
    try {
      await purchaseOrderApi.updateStatus(id, { 
        status: newStatus,
        approved_by: 'Admin' // In real app, this would be the logged-in user
      });
      toast.success(`Order ${newStatus === 'approved' ? 'approved' : 'updated'} successfully`);
      fetchData();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update order status');
    } finally {
      setUpdating(false);
    }
  };

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const res = await purchaseOrderApi.downloadPdf(id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${order.po_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded successfully');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Failed to download PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleNotifyVendor = async () => {
    setNotifyingVendor(true);
    try {
      const res = await purchaseOrderApi.notifyVendor(id);
      toast.success(`Notification sent to vendor (${res.data.emails.join(', ')})`);
    } catch (error) {
      console.error('Error notifying vendor:', error);
      toast.error(error.response?.data?.detail || 'Failed to notify vendor');
    } finally {
      setNotifyingVendor(false);
    }
  };

  const handleDelete = async () => {
    try {
      await purchaseOrderApi.delete(id);
      toast.success('Order deleted successfully');
      navigate('/purchase-orders');
    } catch (error) {
      console.error('Error deleting order:', error);
      toast.error('Failed to delete order');
    }
  };

  const getNextStatus = () => {
    if (!order) return null;
    const currentIndex = statusFlow.indexOf(order.status);
    if (currentIndex < statusFlow.length - 1) {
      return statusFlow[currentIndex + 1];
    }
    return null;
  };

  const getNextStatusAction = () => {
    const next = getNextStatus();
    switch (next) {
      case 'pending': return 'Submit for Approval';
      case 'approved': return 'Approve Order';
      case 'ordered': return 'Mark as Ordered';
      case 'delivered': return 'Mark as Delivered';
      default: return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-muted-foreground">Loading order...</div>
      </div>
    );
  }

  if (!order) {
    return null;
  }

  const StatusIcon = statusConfig[order.status]?.icon || Clock;

  return (
    <div className="space-y-6" data-testid="view-po-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/purchase-orders')} className="rounded-sm">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-semibold font-heading font-mono-data">{order.po_number}</h1>
              <Badge className={`${statusConfig[order.status]?.class} rounded-none text-xs uppercase tracking-wider`}>
                <StatusIcon className="w-3 h-3 mr-1" />
                {statusConfig[order.status]?.label}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Created {order.created_at ? format(new Date(order.created_at), 'PPP') : '-'}
            </p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button 
            variant="outline" 
            size="sm" 
            className="rounded-sm"
            onClick={handleDownloadPdf}
            disabled={downloadingPdf}
            data-testid="btn-download-pdf"
          >
            <FileDown className="w-4 h-4 mr-2" />
            {downloadingPdf ? 'Downloading...' : 'Download PDF'}
          </Button>
          {vendor?.emails?.length > 0 && (
            <Button 
              variant="outline" 
              size="sm" 
              className="rounded-sm"
              onClick={handleNotifyVendor}
              disabled={notifyingVendor}
              data-testid="btn-notify-vendor"
            >
              <Mail className="w-4 h-4 mr-2" />
              {notifyingVendor ? 'Sending...' : 'Notify Vendor'}
            </Button>
          )}
          {order.status === 'draft' && (
            <Button 
              variant="destructive" 
              size="sm" 
              className="rounded-sm"
              onClick={() => setDeleteDialogOpen(true)}
              data-testid="btn-delete-po"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          )}
          {getNextStatusAction() && (
            <Button 
              className="rounded-sm"
              onClick={() => handleStatusUpdate(getNextStatus())}
              disabled={updating}
              data-testid="btn-update-status"
            >
              {getNextStatusAction()}
            </Button>
          )}
        </div>
      </div>

      {/* Status Progress */}
      <Card className="border border-border shadow-none rounded-sm">
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            {statusFlow.map((status, index) => {
              const currentIndex = statusFlow.indexOf(order.status);
              const isCompleted = index < currentIndex;
              const isCurrent = index === currentIndex;
              const Icon = statusConfig[status]?.icon || Clock;
              
              return (
                <div key={status} className="flex items-center flex-1">
                  <div className={`flex flex-col items-center flex-1 ${index < statusFlow.length - 1 ? 'relative' : ''}`}>
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${
                      isCompleted ? 'bg-green-500 border-green-500 text-white' :
                      isCurrent ? 'border-primary bg-primary/10 text-primary' :
                      'border-muted bg-muted text-muted-foreground'
                    }`}>
                      {isCompleted ? <CheckCircle className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                    </div>
                    <span className={`text-xs mt-2 capitalize ${isCurrent ? 'font-medium text-primary' : 'text-muted-foreground'}`}>
                      {statusConfig[status]?.label?.split(' ')[0]}
                    </span>
                  </div>
                  {index < statusFlow.length - 1 && (
                    <div className={`h-0.5 flex-1 -mt-6 ${isCompleted ? 'bg-green-500' : 'bg-muted'}`} />
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Items */}
          <Card className="border border-border shadow-none rounded-sm">
            <CardHeader className="border-b border-border/50 pb-4">
              <CardTitle className="text-base font-heading">Order Items</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table data-testid="po-items-table">
                <TableHeader>
                  <TableRow className="bg-muted/50 hover:bg-muted/50">
                    <TableHead className="font-medium">Product</TableHead>
                    <TableHead className="font-medium text-right">Unit Price</TableHead>
                    <TableHead className="font-medium text-right">Quantity</TableHead>
                    <TableHead className="font-medium text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {order.items?.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell className="font-medium">{item.product_name}</TableCell>
                      <TableCell className="text-right font-mono-data">
                        ${item.unit_price.toLocaleString('en-AU', { minimumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell className="text-right font-mono-data">{item.quantity}</TableCell>
                      <TableCell className="text-right font-mono-data font-semibold">
                        ${item.total.toLocaleString('en-AU', { minimumFractionDigits: 2 })}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-muted/30">
                    <TableCell colSpan={3} className="text-right font-medium">Total Amount</TableCell>
                    <TableCell className="text-right font-mono-data font-semibold text-lg">
                      ${order.total_amount.toLocaleString('en-AU', { minimumFractionDigits: 2 })}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Notes */}
          {order.notes && (
            <Card className="border border-border shadow-none rounded-sm">
              <CardHeader className="border-b border-border/50 pb-4">
                <CardTitle className="text-base font-heading">Notes</CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">{order.notes}</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Vendor Details */}
          <Card className="border border-border shadow-none rounded-sm">
            <CardHeader className="border-b border-border/50 pb-4">
              <CardTitle className="text-base font-heading">Vendor</CardTitle>
            </CardHeader>
            <CardContent className="pt-4 space-y-3">
              <div>
                <p className="font-medium">{order.vendor_name}</p>
                {vendor && (
                  <>
                    <p className="text-sm text-muted-foreground font-mono-data mt-1">ABN: {vendor.abn}</p>
                    {vendor.emails?.length > 0 && (
                      <p className="text-sm text-muted-foreground mt-1">{vendor.emails[0]}</p>
                    )}
                    {vendor.phone && (
                      <p className="text-sm text-muted-foreground font-mono-data mt-1">{vendor.phone}</p>
                    )}
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Delivery Details */}
          <Card className="border border-border shadow-none rounded-sm">
            <CardHeader className="border-b border-border/50 pb-4">
              <CardTitle className="text-base font-heading">Delivery Details</CardTitle>
            </CardHeader>
            <CardContent className="pt-4 space-y-3">
              <div className="flex items-center gap-2">
                {order.delivery_type === 'delivery' ? (
                  <Truck className="w-4 h-4 text-muted-foreground" />
                ) : (
                  <Package className="w-4 h-4 text-muted-foreground" />
                )}
                <span className="capitalize">{order.delivery_type}</span>
              </div>
              
              {order.installer_name && (
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-muted-foreground" />
                  <span>{order.installer_name}</span>
                </div>
              )}
              
              {order.delivery_date && (
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <span>{format(new Date(order.delivery_date), 'PPP')}</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Approval Info */}
          {order.approved_at && (
            <Card className="border border-border shadow-none rounded-sm">
              <CardHeader className="border-b border-border/50 pb-4">
                <CardTitle className="text-base font-heading">Approval</CardTitle>
              </CardHeader>
              <CardContent className="pt-4 space-y-2">
                <div className="flex items-center gap-2 text-green-600">
                  <CheckCircle className="w-4 h-4" />
                  <span className="text-sm">Approved by {order.approved_by}</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  {format(new Date(order.approved_at), 'PPP p')}
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Purchase Order</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {order.po_number}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="rounded-sm bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
